import React, { useState } from 'react';
import {
    Button,
    Modal,
    Form,
    Input,
    Radio,
    Popconfirm,
    Spin,
    Tag,
    Typography,
    Space,
    Divider,
    Card,
    message,
    Empty,
} from 'antd';
import {
    PlusOutlined,
    DeleteOutlined,
    ClockCircleOutlined,
    FileTextOutlined,
} from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import {
    listPropertyNotes,
    createPermanentNote,
    archiveNote,
} from '../../../config/services/patrolService';
import { parseApiError } from '../../../config/services/errorHandler';
import { useSelector } from 'react-redux';
import useColors from '../../../hooks/useColors';
import dayjs from 'dayjs';

const { Text, Paragraph } = Typography;
const { TextArea } = Input;

/**
 * PropertyNotesTab — shown inside Property Detail Tabs.
 *
 * Props:
 *   propertyId  {number}
 */
const PropertyNotesTab = ({ propertyId }) => {
    const { colorPrimary } = useColors();
    const queryClient = useQueryClient();
    const user = useSelector((state) => state.AppReducer?.user);
    const isRole = user?.userRole?.[0];
    const canWrite = isRole === 'admin' || isRole === 'manager';

    const [modalOpen, setModalOpen] = useState(false);
    const [form] = Form.useForm();

    const { data: notes = [], isLoading } = useQuery(
        ['property-notes', propertyId],
        () => listPropertyNotes(propertyId),
        { enabled: !!propertyId }
    );

    const permanentNotes = notes.filter((n) => n.type === 'permanent');
    const passDownNotes = notes.filter((n) => n.type === 'pass_down');

    // ── Create permanent note ─────────────────────────────────
    const createMutation = useMutation(
        (body) => createPermanentNote(propertyId, body),
        {
            onSuccess: () => {
                message.success('Note added.');
                queryClient.invalidateQueries(['property-notes', propertyId]);
                setModalOpen(false);
                form.resetFields();
            },
            onError: (err) => {
                const parsed = parseApiError(err);
                message.error(parsed.message);
            },
        }
    );

    // ── Archive note ──────────────────────────────────────────
    const archiveMutation = useMutation(
        (noteId) => archiveNote(propertyId, noteId),
        {
            onSuccess: () => {
                message.success('Note archived.');
                queryClient.invalidateQueries(['property-notes', propertyId]);
            },
            onError: (err) => {
                const parsed = parseApiError(err);
                message.error(parsed.message);
            },
        }
    );

    const handleCreate = (values) => {
        createMutation.mutate({ body: values.body, type: values.type });
    };

    const NoteCard = ({ note }) => {
        const isPassDown = note.type === 'pass_down';
        const isExpired = isPassDown && note.visibleUntil && dayjs().isAfter(dayjs(note.visibleUntil));

        return (
            <Card
                size="small"
                style={{
                    marginBottom: 10,
                    border: isPassDown ? '1px dashed #d1d5db' : '1px solid #e5e7eb',
                    borderLeft: isPassDown ? '3px solid #f59e0b' : '3px solid #6366f1',
                    opacity: isExpired ? 0.6 : 1,
                }}
            >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                    <div style={{ flex: 1 }}>
                        {/* Render as plain text — sanitised, no HTML */}
                        <Paragraph
                            style={{ marginBottom: 6, whiteSpace: 'pre-wrap', fontSize: 13 }}
                        >
                            {note.body}
                        </Paragraph>
                        <Space size={8} wrap>
                            <Text type="secondary" style={{ fontSize: 11 }}>
                                By {note.author?.name ?? 'Unknown'} · {dayjs(note.createdAt).format('DD MMM YYYY, HH:mm')}
                            </Text>
                            {isPassDown && note.visibleUntil && (
                                <Tag
                                    icon={<ClockCircleOutlined />}
                                    color={isExpired ? 'red' : 'orange'}
                                    style={{ fontSize: 10 }}
                                >
                                    {isExpired ? 'Guard visibility expired' : `Visible until ${dayjs(note.visibleUntil).format('DD MMM YYYY')}`}
                                </Tag>
                            )}
                        </Space>
                    </div>
                    {canWrite && (
                        <Popconfirm
                            title="Archive this note?"
                            description="The note will be removed from active views but not permanently deleted."
                            okText="Archive"
                            onConfirm={() => archiveMutation.mutate(note.id)}
                        >
                            <Button
                                size="small"
                                icon={<DeleteOutlined />}
                                type="text"
                                loading={archiveMutation.isLoading}
                                title="Archive note"
                            />
                        </Popconfirm>
                    )}
                </div>
            </Card>
        );
    };

    return (
        <Spin spinning={isLoading}>
            {/* Permanent notes */}
            <div style={{ marginBottom: 24 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                    <Space>
                        <FileTextOutlined style={{ color: '#6366f1' }} />
                        <Text strong>Permanent Notes</Text>
                        <Text type="secondary" style={{ fontSize: 12 }}>({permanentNotes.length})</Text>
                    </Space>
                    {canWrite && (
                        <Button
                            type="primary"
                            icon={<PlusOutlined style={{ color: '#fff' }} />}
                            onClick={() => setModalOpen(true)}
                            style={{
                                backgroundColor: colorPrimary,
                                border: 'none',
                                color: '#fff',
                                height: '36px',
                                borderRadius: '6px',
                                fontWeight: 500,
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '6px',
                                boxShadow: 'none',
                            }}
                        >
                            Add Note
                        </Button>
                    )}
                </div>
                <Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 10 }}>
                    Permanent notes are visible to guards during active patrols.
                </Text>
                {permanentNotes.length === 0 ? (
                    <Empty description="No permanent notes" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                ) : (
                    permanentNotes.map((n) => <NoteCard key={n.id} note={n} />)
                )}
            </div>

            <Divider />

            {/* Pass-down notes */}
            <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                    <ClockCircleOutlined style={{ color: '#f59e0b' }} />
                    <Text strong>Pass-down Notes</Text>
                    <Text type="secondary" style={{ fontSize: 12 }}>({passDownNotes.length})</Text>
                </div>
                <Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 10 }}>
                    Time-limited notes for guard shift hand-offs. Created by guards during patrols.
                </Text>
                {passDownNotes.length === 0 ? (
                    <Empty description="No pass-down notes" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                ) : (
                    passDownNotes.map((n) => <NoteCard key={n.id} note={n} />)
                )}
            </div>

            {/* Create note modal */}
            <Modal
                title="Add Note"
                open={modalOpen}
                onCancel={() => { setModalOpen(false); form.resetFields(); }}
                footer={null}
                width={520}
                destroyOnClose
            >
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleCreate}
                    initialValues={{ type: 'permanent' }}
                >
                    <Form.Item
                        label="Type"
                        name="type"
                        rules={[{ required: true, message: 'Required.' }]}
                    >
                        <Radio.Group>
                            <Radio value="permanent">Permanent</Radio>
                            <Radio value="pass_down">Pass-down</Radio>
                        </Radio.Group>
                    </Form.Item>
                    <Form.Item
                        label="Note Content"
                        name="body"
                        rules={[
                            { required: true, message: 'Note content is required.' },
                            { max: 10000, message: 'Maximum 10,000 characters.' },
                        ]}
                    >
                        <TextArea
                            rows={6}
                            placeholder="Write the note content here. It will be visible to guards during patrols."
                            maxLength={10000}
                            showCount
                        />
                    </Form.Item>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                        <Button onClick={() => { setModalOpen(false); form.resetFields(); }}>Cancel</Button>
                        <Button type="primary" htmlType="submit" loading={createMutation.isLoading}>
                            Add Note
                        </Button>
                    </div>
                </Form>
            </Modal>
        </Spin>
    );
};

export default PropertyNotesTab;
